import type { ParsedOrder, SoapSection } from "./mockAiService";

/**
 * Client for the deployed Application API (persistence + confirmation + audit).
 *
 * Every call is best-effort: if the API base is not configured or unreachable,
 * functions return null and the UI falls back to its client-only demo flow, so
 * the workspace never breaks.
 */

function base(): string | undefined {
  if (typeof window === "undefined") return undefined;
  const cfg = window.__SATI_CONFIG__;
  if (cfg?.appApiEndpoint) return cfg.appApiEndpoint.replace(/\/+$/, "");
  if (cfg?.agentEndpoint) return cfg.agentEndpoint.replace(/\/agent\/?$/, "");
  // Local development default (app-api dev server).
  if (window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1") {
    return "http://localhost:3002";
  }
  return undefined;
}

export function isPersistenceEnabled(): boolean {
  return Boolean(base());
}

async function post<T>(path: string, body: unknown): Promise<T | null> {
  const apiBase = base();
  if (!apiBase) return null;
  try {
    const response = await fetch(`${apiBase}${path}`, {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify(body),
    });
    if (!response.ok) return null;
    return (await response.json()) as T;
  } catch {
    return null;
  }
}

async function get<T>(path: string): Promise<T | null> {
  const apiBase = base();
  if (!apiBase) return null;
  try {
    const response = await fetch(`${apiBase}${path}`);
    if (!response.ok) return null;
    return (await response.json()) as T;
  } catch {
    return null;
  }
}

export async function ensureEncounter(): Promise<string | null> {
  const result = await post<{ encounterId: string }>("/api/encounters/demo", {});
  return result?.encounterId ?? null;
}

export type SavedNote = { noteDraftId: string; version: number; contentHash: string };

export async function saveNote(
  encounterId: string,
  sections: Record<SoapSection, string>,
  noteDraftId?: string,
): Promise<SavedNote | null> {
  const filtered = Object.fromEntries(
    Object.entries(sections).filter(([, value]) => value.trim()),
  );
  return post<SavedNote>("/api/notes", { encounterId, noteDraftId, sections: filtered });
}

export type SavedOrders = { orderDraftId: string; itemCount: number };

export async function saveOrders(
  encounterId: string,
  orders: ParsedOrder[],
  takeOrder: boolean,
): Promise<SavedOrders | null> {
  if (!orders.length) return null;
  return post<SavedOrders>("/api/orders", {
    encounterId,
    takeOrder,
    orders: orders.map((order) => ({
      category: order.category,
      label: order.label,
      details: order.details,
      lane: order.lane,
      stat: order.stat,
      source: order.source ?? "parsed",
      conflict: order.conflict,
    })),
  });
}

export type Confirmation = {
  idempotentReplay: boolean;
  confirmation: { id: string; contentHash: string; status: string };
};

export async function confirm(
  encounterId: string,
  subjectType: "note" | "order",
  subjectId: string,
  content: unknown,
  idempotencyKey: string,
  takeOrder: boolean,
): Promise<Confirmation | null> {
  return post<Confirmation>("/api/confirmations", {
    encounterId,
    subjectType,
    subjectId,
    content,
    idempotencyKey,
    takeOrder,
  });
}

export type AuditResult = {
  events: Array<{ seq: number; action: string; hash: string }>;
  verification: { valid: boolean; count?: number };
};

export async function getAudit(encounterId: string): Promise<AuditResult | null> {
  return get<AuditResult>(`/api/audit?encounterId=${encodeURIComponent(encounterId)}`);
}
